/** Chase camera maths. Pure. */

/** Exponential smoothing that gives the same result regardless of frame rate. */
export function damp(current: number, target: number, lambda: number, dt: number): number {
  return target + (current - target) * Math.exp(-lambda * dt);
}

/** Field of view in degrees: 60 at rest, widening smoothly to 74 at 25 m/s and beyond. */
export function chaseFov(speed: number): number {
  const t = Math.min(1, Math.max(0, Math.abs(speed) / 25));
  // (Widened only a little with speed: 74 deg at 90 km/h made the ride feel fast-forwarded, owner 2026-10-05.)
  return 60 + 4 * t * t * (3 - 2 * t);
}

/** Camera distance behind and height above the bike; pulls back a little with speed. */
export function chaseOffset(speed: number): { back: number; up: number } {
  const t = Math.min(1, Math.abs(speed) / 25);
  return { back: 4.4 + 1.8 * t, up: 1.75 + 0.35 * t };
}

/** Ride cameras the C key / View button / HUD button cycles through. */
export type CameraView = 'chase' | 'fpv';
export const CAMERA_VIEWS: readonly CameraView[] = ['chase', 'fpv'];
export const nextCameraView = (v: CameraView): CameraView => CAMERA_VIEWS[(CAMERA_VIEWS.indexOf(v) + 1) % CAMERA_VIEWS.length]!;

/**
 * Helmet camera horizontal field of view in degrees: 110 at rest, 114 at 25 m/s and beyond. Wide like an onboard
 * action camera: from a rider's eyes the bars are 50-60 degrees below the line of sight, and a narrower view
 * cannot hold both them and the track ahead.
 */
export function fpvFov(speed: number): number {
  const t = Math.min(1, Math.max(0, Math.abs(speed) / 25));
  return 110 + 4 * t * t * (3 - 2 * t);
}

/** Vertical FOV (three's camera.fov) for a horizontal one at this aspect, kept within 55-85 (wide or tall screens). */
export function verticalFov(horizontalDeg: number, aspect: number): number {
  const v = (2 * Math.atan(Math.tan((horizontalDeg * Math.PI) / 360) / aspect) * 180) / Math.PI;
  return Math.min(85, Math.max(55, v));
}

/** Share of the head's roll the helmet camera follows (the rest is held level with the horizon). */
export const FPV_ROLL_FOLLOW = 0.25; // QA 2026-10-03 B8: 0.6 tilted the horizon ~20 deg in a lean
